// src/components/VisitStepper.tsx
// One ~40px sticky bar, everywhere in the visit pipeline — visit id,
// completed/skipped stages as small clickable ✓+label chips, the current
// stage as an accent-bordered pill, upcoming stages as muted (or, for a
// predicted skip, struck-through) text, Previous stage + Close on the
// right. Replaced the old two-tier design (a navy visit-id bar plus a much
// taller icon-circle stage row, further forked by phone/tablet/desktop)
// once the Consultation redesign's compact stepper (2026-09-17) proved out
// well enough to use for the whole pipeline, not just that one stage —
// every stage benefits from the extra vertical room, and a single dense
// row scrolls horizontally on a narrow screen just as well as it does on
// desktop, so the old phone-only vertical timeline and tablet-narrow sizing
// aren't needed either.
import { Fragment } from 'react';

export interface StageMeta {
  id: string;
  label: string;
  icon: string;
  desc: string;
}

// Pharmacy/Billing are skipped server-side for an IP-bound visit (see
// backend/routes/visits.js) — an admitted patient's dispensing/billing runs
// continuously through the stay instead, settled at ward discharge.
export const STAGES: StageMeta[] = [
  { id:'verify',       label:'Verify',       icon:'🔍', desc:'Identify patient' },
  { id:'register',     label:'Register',     icon:'📝', desc:'Demographics'     },
  { id:'vitals',       label:'Vitals',       icon:'❤️',  desc:'Clinical vitals'  },
  { id:'pre_consult',  label:'Pre-Consult',  icon:'🩺', desc:'Nurse triage'     },
  { id:'queue',        label:'Queue',        icon:'🎫', desc:'Assign token'     },
  { id:'consultation', label:'Consultation', icon:'👨‍⚕️', desc:'Doctor consult & OP/IP decision' },
  { id:'ip',           label:'IP',           icon:'🛏', desc:'Ward admission'   },
  { id:'pharmacy',     label:'Pharmacy',     icon:'💊', desc:'Dispense medicines' },
  { id:'billing',      label:'Billing',      icon:'🧾', desc:'Generate bill'    },
  { id:'resolve',      label:'Resolve',      icon:'✅', desc:'Close visit'      },
];

export type StageStatus = 'pending' | 'active' | 'completed' | 'skipped';

export interface VisitStepperProps {
  currentStage: string;
  stageStatuses?: Record<string, StageStatus | string>;
  visitId?: string | null;
  onStageClick?: (stageId: string) => void;
  onClose?: (() => void) | null;
  canRollback?: boolean;
  onRollback?: () => void;
  /** Stage ids the doctor's in-progress (not yet submitted) choices already
   * imply will be skipped — e.g. picking Outpatient means IP won't run. Only
   * meaningful for a stage still ahead (never overrides an isActive/
   * isResolved render); wired from ConsultStage's reportPredictedStageSkips
   * today, since Consultation is the one stage whose own live selection
   * determines a skip ahead of the backend's real advanceStage() decision.
   * Distinct from the server-driven 'skipped' StageStatus, which reflects a
   * decision already recorded on advance. */
  predictedSkip?: string[];
}

export default function VisitStepper({ currentStage, stageStatuses = {}, visitId, onStageClick, onClose, canRollback, onRollback, predictedSkip = [] }: VisitStepperProps) {
  const stageMeta = STAGES.map(stage => {
    const status = stageStatuses[stage.id] || 'pending';
    return {
      stage,
      isActive: stage.id === currentStage,
      // A skipped stage (Verify/Register for a returning patient, Pre-Consult
      // when the nurse picked "None", Pharmacy/Billing on an IP visit) covers
      // the same ground through a different path — it's resolved, not
      // unfinished, so it reads as a checkmark like a completed stage rather
      // than a dash that looks like nothing happened there.
      isResolved: status === 'completed' || status === 'skipped',
      canClick: status === 'completed' || status === 'skipped' || stage.id === currentStage,
    };
  });

  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 100,
      // var(--navy-dark), not --navy itself and not a fixed neutral —
      // --navy is Admin Panel → Branding's own tenant color and isn't
      // guaranteed dark (a hospital can pick a bright/light navy_color),
      // but a flat generic slate would throw that hospital's identity
      // away entirely just to fix the darkness. --navy-dark (useBranding's
      // injectCSS, via lib/color.ts's ensureDark) is the same hue, forced
      // dark — a hospital that already picked a proper dark navy gets it
      // back unchanged; one that didn't still gets a bar that's clearly
      // *theirs*, not generic chrome.
      background: 'var(--navy-dark)',
      boxShadow: '0 1px 6px rgba(0,0,0,.25)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, height: 40,
        padding: '0 14px',
      }}>
        {visitId && (
          <span title={visitId} style={{
            // --accent-on-navy is contrast-checked against --navy, which is
            // always at least as light as the --navy-dark this bar actually
            // paints — darkening the background can only help that
            // contrast, never hurt it, so the same token used elsewhere for
            // accent text on navy chrome (App.tsx's sidebar) applies here.
            // Allowed to shrink (unlike the chips/buttons either side, which
            // stay fixed) and ellipsis rather than reserving its full
            // ~145px unconditionally — on a phone-width viewport that alone
            // was enough to squeeze the scrollable stage chips section to
            // 0px. The full id is still readable via the title tooltip.
            fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 700,
            color: 'var(--accent-on-navy)', flexShrink: 1, minWidth: 40, maxWidth: 160,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {visitId}
          </span>
        )}

        {/* Its own scroll container, separate from the visitId/buttons either
            side of it — those two must stay fixed-visible at all times (the
            visit id for orientation, Close/Previous stage as the one thing
            on this bar staff reach for), so only the chips themselves scroll
            on a screen too narrow to fit all ten. Putting the whole row
            (chips + the margin-left:auto button group) in one overflow-x:
            auto container instead pushes the buttons using the container's
            own width, not its scrollable content width — on a narrow screen
            that visually collides them with whichever chip happens to sit
            at that x position, rather than scrolling them to the true end.
            flex: '1 1 0%', not '1 1 auto' — with an 'auto' basis, Chromium
            sizes this item off its own huge intrinsic content width (all
            ten chips laid out at once) before applying flex-shrink, and on
            a narrow screen that calculation bottoms it out at 0 instead of
            the ~correct remaining space. Starting the basis at 0% and
            growing from there is the standard fix (equivalent to Tailwind's
            "flex-1") and is what actually gives this section the leftover
            width predictably. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0, flex: '1 1 0%', overflowX: 'auto' }}>
          {stageMeta.map(({ stage, isActive, isResolved, canClick }, idx) => (
            <Fragment key={stage.id}>
              {idx > 0 && <span style={{ color: 'rgba(255,255,255,.3)', fontSize: 12, flexShrink: 0 }}>›</span>}
              {isActive ? (
                // The border stays --accent — the hospital's *alternate*
                // brand color (Admin Panel → Branding), not --teal/
                // --primary which the rest of the app already leans on
                // everywhere — rather than a fixed hex, so a re-theme
                // (new tenant, new colors) never needs a code change here.
                // The label text itself is plain white rather than
                // --accent: some accent hues (this tenant's pink included)
                // don't carry a 12px bold label as crisply as white does,
                // and the border alone is enough to identify which stage
                // is current without the label needing to match its hue.
                <span style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  fontSize: 12, fontWeight: 800, letterSpacing: '.1px', color: '#fff',
                  background: 'rgba(255,255,255,.1)', border: '1.5px solid var(--accent)',
                  borderRadius: 999, padding: '2px 9px', whiteSpace: 'nowrap', flexShrink: 0,
                }}>
                  {stage.icon} {stage.label}
                </span>
              ) : isResolved ? (
                <button
                  onClick={() => canClick && onStageClick?.(stage.id)}
                  title={`${stage.label} — ${stage.desc}`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    background: 'rgba(255,255,255,.08)', border: 'none', borderRadius: 999,
                    padding: '2px 8px 2px 2px', flexShrink: 0,
                    cursor: canClick ? 'pointer' : 'default',
                  }}
                >
                  <span style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                    background: 'var(--teal)', color: '#fff', fontSize: 10,
                  }}>
                    ✓
                  </span>
                  <span style={{ fontSize: 11.5, fontWeight: 600, color: 'rgba(255,255,255,.85)', whiteSpace: 'nowrap' }}>
                    {stage.label}
                  </span>
                </button>
              ) : predictedSkip.includes(stage.id) ? (
                // Not the server-driven 'skipped' status (that's the ✓
                // branch above, via isResolved) — this is the doctor's own
                // in-progress, not-yet-submitted choice already implying
                // this stage won't run (e.g. Outpatient picked → IP is
                // predicted-skip). Struck through rather than just muted,
                // so it visibly reads as "won't happen" and not merely
                // "hasn't happened yet" like a genuinely pending stage.
                <span title={`${stage.label} will be skipped based on the disposition picked`} style={{
                  fontSize: 11, color: 'rgba(255,255,255,.25)', flexShrink: 0, whiteSpace: 'nowrap',
                  textDecoration: 'line-through', fontStyle: 'italic',
                }}>
                  {stage.label}
                </span>
              ) : (
                <span style={{
                  fontSize: 11, color: 'rgba(255,255,255,.35)', flexShrink: 0, whiteSpace: 'nowrap',
                }}>
                  {stage.label}
                </span>
              )}
            </Fragment>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          {canRollback && (
            // "← Back", not "← Previous stage" — the full sentence is still
            // there as the title tooltip, but at a phone-width viewport
            // this bar's fixed-width pieces (visit id + these two buttons)
            // are competing with the scrollable stage chips for a single
            // ~340px content row; the verbose label alone left ~0px for the
            // chips to scroll into view at all.
            <button onClick={onRollback} title="Send this visit back to its previous stage"
              style={{
                background: 'rgba(255,255,255,.1)', border: '1px solid rgba(255,255,255,.25)',
                color: 'rgba(255,255,255,.9)', borderRadius: 'var(--radius)', cursor: 'pointer',
                fontSize: 11, fontWeight: 600, padding: '3px 9px', whiteSpace: 'nowrap',
              }}>
              ← Back
            </button>
          )}
          {onClose && (
            <button onClick={onClose} title="Close this visit and return to the visit list"
              style={{
                background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.3)',
                color: '#fff', borderRadius: 'var(--radius)', cursor: 'pointer',
                fontSize: 11, fontWeight: 600, padding: '3px 9px', whiteSpace: 'nowrap',
              }}>
              ✕ Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
